import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Popover, Space, Tag, Tooltip, Typography } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DATE_STRIP_WINDOW = 90; // days rendered either side of today
const DATE_STRIP_DAYS = DATE_STRIP_WINDOW * 2 + 1;
const SLOT_ITEM_MIN_WIDTH = 60;

/**
 * Parse a trade-job date string (DD-MM-YYYY or ISO) to "YYYY-MM-DD".
 */
const toIso = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(raw)) {
    const [dd, mm, yyyy] = raw.split('-');
    return `${yyyy}-${mm}-${dd}`;
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

/**
 * Derive a start-hour number from a job's slots or starttime field.
 */
const getStartHour = (job) => {
  const slotStr =
    (Array.isArray(job.slots) && job.slots.length > 0 ? job.slots[0] : null) ||
    job.starttime ||
    job.time ||
    '';
  const match = String(slotStr).match(/^(\d{1,2}):/);
  return match ? parseInt(match[1], 10) : null;
};

export default function CalendarView({
  jobs,
  loading,
  onRefresh,
  onEditJob,
  currentUserId,
}) {
  const today = useMemo(() => dayjs(), []);

  // Build fixed date array centred on today
  const dates = useMemo(
    () =>
      Array.from({ length: DATE_STRIP_DAYS }, (_, i) =>
        today.subtract(DATE_STRIP_WINDOW, 'day').add(i, 'day')
      ),
    [today]
  );

  const [selectedDate, setSelectedDate] = useState(today);
  const stripRef = useRef(null);

  const todayIndex = useMemo(
    () => dates.findIndex((d) => d.isSame(today, 'day')),
    [dates, today]
  );

  // Scroll strip to keep selected date visible
  const scrollToIndex = useCallback(
    (index) => {
      const container = stripRef.current;
      if (!container) return;
      const itemEl = container.children[index];
      if (!itemEl) return;
      const offset =
        itemEl.offsetLeft -
        container.offsetWidth / 2 +
        itemEl.offsetWidth / 2;
      container.scrollTo({ left: offset, behavior: 'smooth' });
    },
    []
  );

  useEffect(() => {
    scrollToIndex(todayIndex);
  }, [scrollToIndex, todayIndex]);

  const handleSelectDate = (date, index) => {
    setSelectedDate(date);
    scrollToIndex(index);
  };

  const goToPrevDay = () => {
    const newDate = selectedDate.subtract(1, 'day');
    const index = dates.findIndex((d) => d.isSame(newDate, 'day'));
    if (index >= 0) handleSelectDate(newDate, index);
  };

  const goToNextDay = () => {
    const newDate = selectedDate.add(1, 'day');
    const index = dates.findIndex((d) => d.isSame(newDate, 'day'));
    if (index >= 0) handleSelectDate(newDate, index);
  };

  const goToToday = () => {
    handleSelectDate(today, todayIndex);
  };

  // Jobs for the selected date keyed by start-hour
  const bookingsByHour = useMemo(() => {
    const isoSelected = selectedDate.format('YYYY-MM-DD');
    const map = {};
    jobs.forEach((job) => {
      const isoDate = toIso(job.selectedDateString || job.date);
      if (isoDate !== isoSelected) return;
      const hour = getStartHour(job);
      if (hour === null) return;
      if (!map[hour]) map[hour] = [];
      map[hour].push(job);
    });
    return map;
  }, [jobs, selectedDate]);

  const isToday = selectedDate.isSame(today, 'day');

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={0}>
      {/* ── Controls row ── */}
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }} wrap>
        <Space>
          <Button icon={<LeftOutlined />} onClick={goToPrevDay} />
          <Button onClick={goToToday} type={isToday ? 'primary' : 'default'}>
            Today
          </Button>
          <Button icon={<RightOutlined />} onClick={goToNextDay} />
        </Space>
        <Text strong style={{ fontSize: 15 }}>
          {selectedDate.format('ddd D MMMM YYYY')}
        </Text>
        <Button onClick={onRefresh} loading={loading}>Refresh</Button>
      </Space>

      {/* ── Date strip ── */}
      <div
        ref={stripRef}
        style={{
          display: 'flex',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          gap: 4,
          padding: '4px 2px 8px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        {dates.map((date, index) => {
          const isSelected = date.isSame(selectedDate, 'day');
          const isTodayDate = date.isSame(today, 'day');
          const hasJobs = jobs.some(
            (j) => toIso(j.selectedDateString || j.date) === date.format('YYYY-MM-DD')
          );

          return (
            <div
              key={date.format('YYYY-MM-DD')}
              role="button"
              tabIndex={0}
              onClick={() => handleSelectDate(date, index)}
              onKeyDown={(e) => e.key === 'Enter' && handleSelectDate(date, index)}
              style={{
                minWidth: SLOT_ITEM_MIN_WIDTH,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '6px 4px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: isSelected ? '#1677ff' : 'transparent',
                border: isTodayDate && !isSelected ? '1px solid #1677ff' : '1px solid transparent',
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <Text
                style={{
                  fontSize: 11,
                  color: isSelected ? '#fff' : '#8c8c8c',
                  lineHeight: 1.2,
                }}
              >
                {DAYS_SHORT[date.day()]}
              </Text>
              <Text
                style={{
                  fontSize: 17,
                  fontWeight: isSelected || isTodayDate ? 700 : 400,
                  color: isSelected ? '#fff' : '#262626',
                }}
              >
                {date.date()}
              </Text>
              {hasJobs && (
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: isSelected ? '#fff' : '#1677ff',
                    marginTop: 3,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Hourly timeline ── */}
      <div style={{ marginTop: 8 }}>
        {HOURS.map((hour) => {
          const bookingsInSlot = bookingsByHour[hour] || [];
          const label = `${String(hour).padStart(2, '0')}:00`;

          return (
            <div
              key={hour}
              style={{
                display: 'grid',
                gridTemplateColumns: '52px 1fr',
                minHeight: 48,
                borderBottom: '1px solid #f5f5f5',
                alignItems: 'flex-start',
              }}
            >
              {/* Hour label */}
              <div
                style={{
                  paddingTop: 6,
                  paddingRight: 8,
                  textAlign: 'right',
                  color: '#8c8c8c',
                  fontSize: 12,
                  userSelect: 'none',
                  flexShrink: 0,
                }}
              >
                {label}
              </div>

              {/* Booking cards */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  padding: '4px 0 4px 8px',
                  borderLeft: '2px solid #f0f0f0',
                  minHeight: 48,
                  alignItems: 'flex-start',
                }}
              >
                {bookingsInSlot.map((job) => (
                  <BookingCard
                    key={job.id || job.key}
                    job={job}
                    onEdit={onEditJob}
                    currentUserId={currentUserId}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Space>
  );
}

const STATUS_COLOR = {
  Initial: 'default',
  Inspection: 'processing',
  Quote: 'blue',
  'In-progress': 'orange',
  Invoice: 'gold',
  Completed: 'success',
  Incomplete: 'error',
  Cancelled: 'volcano',
};

function BookingCard({ job, onEdit, currentUserId }) {
  const name =
    job.jobName || job.title || '(Unnamed job)';
  const customer = job.customerName || '';
  const assignee = job.assignee || job.assigneeName || job.staffName || '';
  const slots = Array.isArray(job.slots) && job.slots.length > 0
    ? job.slots.slice(0, 3).join(', ') + (job.slots.length > 3 ? '…' : '')
    : job.starttime || '';

  const isMyJob = currentUserId && String(job.assigneeId) === String(currentUserId);

  const content = (
    <Space direction="vertical" size={4} style={{ maxWidth: 260 }}>
      <Text strong>{name}</Text>
      {customer ? <Text type="secondary">Customer: {customer}</Text> : null}
      {assignee ? <Text type="secondary">Assignee: {assignee}</Text> : null}
      {slots ? <Text type="secondary">Slots: {slots}</Text> : null}
      {job.jobAddress ? <Text type="secondary">Address: {job.jobAddress}</Text> : null}
      <Tag color={STATUS_COLOR[job.status] || 'default'} style={{ marginTop: 4 }}>
        {job.status || 'Unknown'}
      </Tag>
      {onEdit ? (
        <Button
          size="small"
          type="primary"
          style={{ marginTop: 6, width: '100%' }}
          onClick={() => onEdit(job)}
        >
          Edit job
        </Button>
      ) : null}
    </Space>
  );

  return (
    <Popover content={content} trigger="click" placement="topLeft">
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && null}
        style={{
          background: isMyJob ? '#e6f4ff' : '#f6ffed',
          border: `1px solid ${isMyJob ? '#91caff' : '#b7eb8f'}`,
          borderRadius: 8,
          padding: '4px 10px',
          cursor: 'pointer',
          maxWidth: 200,
          minWidth: 100,
          userSelect: 'none',
        }}
      >
        <Tooltip title={`${name}${customer ? ` — ${customer}` : ''}`}>
          <Text
            style={{
              fontSize: 12,
              fontWeight: 600,
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 180,
            }}
          >
            {name}
          </Text>
          {customer ? (
            <Text
              type="secondary"
              style={{
                fontSize: 11,
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 180,
              }}
            >
              {customer}
            </Text>
          ) : null}
        </Tooltip>
      </div>
    </Popover>
  );
}
